import type { RouterPage } from '@wix/editor-platform-sdk-types'
import {
  deletePattern,
  runTransactionWithRetry,
} from '@wix/wix-data-client-common'
import type {
  DynamicPageRouterData,
  EditorSDK,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import { findKey } from 'lodash'

const findPatternForPage = (
  router: DynamicPageRouterData,
  pageRoles: string[],
): string | undefined =>
  findKey(router.config.patterns, p => pageRoles.includes(p.pageRole))

export const removeRedundantRoutesOrRouters = async ({
  editorSdkProxy,
  router,
  validPagesCount,
  invalidPages,
  logger,
}: {
  editorSdkProxy: EditorSDK
  router: DynamicPageRouterData
  validPagesCount: number
  invalidPages: { page: RouterPage }[]
  logger: Logger
}) => {
  if (validPagesCount === 0) {
    const routerRef = await editorSdkProxy.routers.getByPrefix({
      prefix: router.prefix,
    })

    try {
      await editorSdkProxy.routers.remove({ routerRef })
    } catch (e) {
      const error = new Error('Failed to remove corrupted router', {
        cause: e,
      })
      logger.error(error, { routerRef, router })
    }
  } else if (invalidPages.length > 0) {
    const routerRef = await editorSdkProxy.routers.getByPrefix({
      prefix: router.prefix,
    })

    for (const { page } of invalidPages) {
      const pattern = findPatternForPage(router, page.pageRoles)
      try {
        await runTransactionWithRetry(editorSdkProxy, async () => {
          await editorSdkProxy.routers.pages.disconnect({
            routerRef,
            pageRef: page.pageRef,
          })

          if (pattern) {
            await editorSdkProxy.routers.update({
              routerRef,
              config: deletePattern(router.config, pattern),
            })
          }
        })
      } catch (e) {
        const error = new Error('Failed to remove corrupted page from router', {
          cause: e,
        })
        logger.error(error, { router, page, pattern, routerRef })
      }
    }
  }
}
